import { Box, IconButton, Typography } from "@mui/material";
import { useState } from "react";

const COLORS = {
    primary: "#0359D6",
    dark: "#090E34",
    stroke: "#E0E0E0",
    white: "#FFFFFF",
    body: "#637381",
};

/**
 * CalendarGrid
 *
 * Props:
 *   selectedDate: "DD/MM/YYYY" | null — data atualmente selecionada (para highlight)
 *   initialDate: Date — mês inicial de navegação (default: hoje)
 *   onSelectDay(formatted: "DD/MM/YYYY"): chamado ao clicar em um dia
 */
export function CalendarGrid({ selectedDate, onSelectDay, initialDate }) {
    const [navigationDate, setNavigationDate] = useState(
        initialDate ?? new Date(),
    );
    const currentDate = new Date(new Date().setHours(0, 0, 0, 0));

    const year = navigationDate.getFullYear();
    const month = navigationDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const dayCellStyle = (isSelected, isCurrent) => ({
        display: "flex",
        height: "38px",
        width: "38px",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "7px",
        cursor: "pointer",
        fontSize: "14px",
        backgroundColor: isSelected ? COLORS.primary : "transparent",
        color: isSelected ? COLORS.white : COLORS.dark,
        transition: "0.2s",
        border:
            isCurrent && !isSelected ? `1px solid ${COLORS.stroke}` : "none",
        "&:hover": {
            backgroundColor: isSelected ? COLORS.primary : "#dedede",
            transition: "0.2s",
        },
    });

    const handleSelectDay = (day) => {
        const formatted = `${String(day).padStart(2, "0")}/${String(month + 1).padStart(2, "0")}/${year}`;
        onSelectDay(formatted);
    };

    return (
        <Box>
            {/* Navegação de mês */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <IconButton
                    onClick={() =>
                        setNavigationDate(new Date(year, month - 1, 1))
                    }
                    size="small"
                    sx={{
                        border: `1px solid ${COLORS.stroke}`,
                        width: "30px",
                        height: "30px",
                    }}
                >
                    <span
                        style={{
                            fontSize: "16px",
                            color: COLORS.dark,
                            fontWeight: "bold",
                        }}
                    >
                        {"<"}
                    </span>
                </IconButton>
                <Typography
                    sx={{
                        fontWeight: 600,
                        color: COLORS.dark,
                        textTransform: "lowercase",
                        "&::first-letter": { textTransform: "uppercase" },
                    }}
                >
                    {navigationDate.toLocaleDateString("pt-BR", {
                        month: "long",
                        year: "numeric",
                    })}
                </Typography>
                <IconButton
                    onClick={() =>
                        setNavigationDate(new Date(year, month + 1, 1))
                    }
                    size="small"
                    sx={{
                        border: `1px solid ${COLORS.stroke}`,
                        width: "30px",
                        height: "30px",
                    }}
                >
                    <span
                        style={{
                            fontSize: "16px",
                            color: COLORS.dark,
                            fontWeight: "bold",
                        }}
                    >
                        {">"}
                    </span>
                </IconButton>
            </Box>

            {/* Grid de dias */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: 0.5,
                    textAlign: "center",
                }}
            >
                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
                    <Typography
                        key={d}
                        sx={{
                            fontSize: "12px",
                            color: COLORS.body,
                            fontWeight: 600,
                        }}
                    >
                        {d}
                    </Typography>
                ))}
                {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                    <div key={`e-${i}`} />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const formatted = `${String(day).padStart(2, "0")}/${String(month + 1).padStart(2, "0")}/${year}`;
                    const isSelected = selectedDate === formatted;
                    const isCurrent =
                        currentDate.getTime() ===
                        new Date(year, month, day).getTime();
                    return (
                        <Box
                            key={day}
                            sx={dayCellStyle(isSelected, isCurrent)}
                            onClick={() => handleSelectDay(day)}
                        >
                            {day}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
